import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { tourStops, TourStop } from 'fym-shared/src/music24/onboardingTour';
import type { OnboardingStateAnswer } from '../../../../../music24-api/src/trpc/onboarding.router';
import { useTRPCAPI, useTRPCClientAPI } from '../../../trpc/trpc';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { useTourActive } from '../../../redux/slices/appInfo/hooks';
import { setTourActive } from '../../../redux/slices/appInfo/slice';
import { useShowToast } from '../../../redux/slices/toasts/hooks';
import { ToastType } from '../../../redux/slices/toasts/state';
import { SMLAnalytics } from '../../analytics/sml/analytics';
import { useArtistSwitcher } from '../../accountSelector/useArtistSwitcher';
import { DEMO_ACCOUNT_ID, firstOwnAccountId } from '../../accountSelector/demoAccounts';
import { useOnboardingState } from '../useOnboardingState';
import { onboardingIntentOf } from '../signupIntent';
import { tourRoute } from './tourRoutes';
import { useDemoSongUuid } from './useDemoSongUuid';
import { storeTourActive } from './tourSession';
import { beginTourStepCall, clearTourPendingStep, endTourStepCall, useTourPendingStep } from './tourPendingStep';
import { TOUR_ADD_SONG_TOAST_ACTION } from './useTourToastActions';
import { tourMessages } from './tourMessages';

export interface TourControls {
  active: boolean;
  /** The current stop; null while the tour does not run or the server answer loads. */
  stop: TourStop | null;
  /** The position of the stop in `tourStops(intent)` (0-based). */
  index: number;
  count: number;
  /** True for a team member: the last stop opens the workspace, not "Add my song". */
  isMember: boolean;
  start: () => void;
  next: () => void;
  back: () => void;
  exit: () => void;
}

/**
 * The LaziiBee tour (design: onboarding b1-b7). `start` selects the demo artist, turns the guide
 * bar on and opens the first stop. The step is stored on the server (`onboarding.setTourStep`),
 * so a reload or another device goes on at the same stop (`useTourResume`). While a step call is
 * in flight, the step of this tab wins over a refetched answer (`tourPendingStep`). The tour sends
 * no write for the demo.
 */
export function useTour(): TourControls {
  const intl = useIntl();
  const dispatch = useDispatch();
  const router = useSMLRouter();
  const trpcAPI = useTRPCAPI();
  const trpcClient = useTRPCClientAPI();
  const queryClient = useQueryClient();
  const { show: showToast } = useShowToast();
  const active = useTourActive();
  const { state } = useOnboardingState();
  const { accounts, onSelect } = useArtistSwitcher();
  const demoSongUuid = useDemoSongUuid(active || state?.tour.finished === false);

  // The intent of this page load counts until the server answer has it: a fast "Continue to demo"
  // then starts at the right stop.
  const intent = onboardingIntentOf(state);
  const stops = useMemo(() => tourStops(intent), [intent]);
  const count = stops.length;
  const pendingStep = useTourPendingStep();
  const index = Math.min(Math.max(pendingStep ?? state?.tour.step ?? 0, 0), count - 1);
  const isMember = state?.isMember === true;

  // The bar follows the cached answer, so a step shows at once; the server call runs behind it.
  const patchTour = useCallback(
    (tour: Partial<OnboardingStateAnswer['tour']>) => {
      queryClient.setQueryData(trpcAPI.onboarding.state.queryKey(), (old: OnboardingStateAnswer | undefined) =>
        old === undefined ? old : { ...old, tour: { ...old.tour, ...tour } }
      );
    },
    [queryClient, trpcAPI]
  );

  const goTo = useCallback(
    (step: number) => {
      const stop = stops[step];
      if (stop === undefined) {
        return;
      }
      patchTour({ step, active: true });
      // A refetch that started before the call can write the old step over the new one. The local
      // step wins while the call is in flight; the cache gets the step again when it ends.
      beginTourStepCall(step);
      // A failed call is silent: the next step sends the step again.
      trpcClient.onboarding.setTourStep
        .mutate({ step })
        .catch(console.error)
        .finally(() => {
          const settled = endTourStepCall();
          if (settled !== null) {
            // A realtime refetch during the call can hold the old step: write the step again.
            patchTour({ step: settled });
          }
        });
      SMLAnalytics.track('onboarding_tour_step', { step, stop }).catch(console.error);
      const { route, params } = tourRoute(stop, demoSongUuid);
      router.push(route, params);
    },
    [demoSongUuid, patchTour, router, stops, trpcClient]
  );

  const end = useCallback(() => {
    clearTourPendingStep();
    const ended = { finished: true, active: false };
    patchTour(ended);
    trpcClient.onboarding.finishTour
      .mutate()
      .catch(console.error)
      // A refetch during the call can say "not finished": write the end again.
      .finally(() => patchTour(ended));
    dispatch(setTourActive({ active: false }));
    storeTourActive(false);
  }, [dispatch, patchTour, trpcClient]);

  const start = useCallback(() => {
    // "Show me an example" is the one explicit choice that selects the demo artist.
    onSelect(DEMO_ACCOUNT_ID);
    dispatch(setTourActive({ active: true }));
    storeTourActive(true);
    goTo(0);
  }, [dispatch, goTo, onSelect]);

  const finish = useCallback(() => {
    SMLAnalytics.track('onboarding_tour_finished', {}).catch(console.error);
    end();
    const ownId = firstOwnAccountId(accounts);
    if (ownId !== null) {
      onSelect(ownId);
    }
    router.push(isMember ? SMLRoutes.home : SMLRoutes.addSong);
  }, [accounts, end, isMember, onSelect, router]);

  const next = useCallback(() => {
    if (index >= count - 1) {
      finish();
      return;
    }
    goTo(index + 1);
  }, [count, finish, goTo, index]);

  const back = useCallback(() => {
    if (index <= 0) {
      return;
    }
    goTo(index - 1);
  }, [goTo, index]);

  // "Exit tour" keeps LaziiBee selected and the page as it is. The toast offers the next step to
  // the owner (design b6); a member has no song to add.
  const exit = useCallback(() => {
    SMLAnalytics.track('onboarding_tour_exit', { step: index }).catch(console.error);
    end();
    showToast({
      type: ToastType.success,
      description: intl.formatMessage(tourMessages.exited),
      autoHide: true,
      action: isMember
        ? undefined
        : { label: intl.formatMessage(tourMessages.addSong), actionId: TOUR_ADD_SONG_TOAST_ACTION },
    });
  }, [end, index, intl, isMember, showToast]);

  const stop = active && state !== undefined ? (stops[index] ?? null) : null;
  return { active, stop, index, count, isMember, start, next, back, exit };
}
